import { FileText, Image as ImageIcon, Loader2, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import type { DeckPlan } from "@/lib/api";

interface DeckPlanReviewProps {
  plan: DeckPlan;
  onApprove: () => void;
  isBuilding: boolean;
}

/**
 * What the composer intends to build, offered before it builds it.
 *
 * This is the cheap half of a deck turn: the plan is deterministic, so it
 * arrives in the time a search takes. Copywriting, translation and the
 * PowerPoint write only happen once someone says yes — which is what makes
 * "not quite, try again" affordable rather than a minute-long round trip.
 */
export function DeckPlanReview({ plan, onApprove, isBuilding }: DeckPlanReviewProps) {
  const assets = plan.assets_used ?? [];
  const sources = assets.filter((asset) => asset.kind !== "image");
  const images = assets.filter((asset) => asset.kind === "image");

  return (
    <section className="border-border bg-surface-muted/60 space-y-4 rounded-lg border p-4">
      <header className="space-y-1">
        <h3 className="font-heading text-foreground text-sm font-bold">
          {plan.slides.length} slides, on {plan.template_name ?? "the VELUX template"}
        </h3>
        <p className="text-muted text-xs leading-5">
          Drawing on {describeSources(sources.length, images.length)}. Nothing has been written yet
          — approve and I&apos;ll write the copy and build the file.
        </p>
      </header>

      <ol className="space-y-1.5">
        {plan.slides.map((slide) => (
          <li
            key={slide.index}
            className="border-border bg-card flex items-start gap-3 rounded-md border px-3 py-2"
          >
            <span className="text-muted mt-0.5 w-4 shrink-0 text-right text-xs tabular-nums">
              {slide.index}
            </span>

            {slide.image_thumbnail_url ? (
              <img
                src={slide.image_thumbnail_url}
                alt=""
                className="border-border h-9 w-12 shrink-0 rounded border object-cover"
                loading="lazy"
              />
            ) : (
              <span className="border-border bg-surface-muted text-faint flex h-9 w-12 shrink-0 items-center justify-center rounded border">
                <ImageIcon className="h-3.5 w-3.5" />
              </span>
            )}

            <div className="min-w-0 flex-1">
              <p className="text-foreground truncate text-sm">
                {slide.title ?? <span className="text-muted italic">copy to be written</span>}
              </p>
              <p className="text-muted mt-0.5 truncate text-[11px]">
                {slide.layout}
                {slide.source_asset_filename ? ` · from ${slide.source_asset_filename}` : ""}
              </p>
            </div>
          </li>
        ))}
      </ol>

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={onApprove} disabled={isBuilding}>
          {isBuilding ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Building the deck…
            </>
          ) : (
            <>
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              Build the deck
            </>
          )}
        </Button>
        {!isBuilding && (
          <p className="text-muted text-xs">
            Or tell me what to change below — fewer slides, another language, a different angle.
          </p>
        )}
      </div>

      {isBuilding && (
        <p className="text-muted flex items-center gap-2 text-xs">
          <FileText className="h-3.5 w-3.5" />
          Writing slide copy in the target language, then assembling the file. This is the slow
          part.
        </p>
      )}
    </section>
  );
}

function describeSources(sources: number, images: number): string {
  const parts: string[] = [];
  if (sources) {
    parts.push(`${sources} source ${sources === 1 ? "document" : "documents"}`);
  }
  if (images) {
    parts.push(`${images} ${images === 1 ? "image" : "images"}`);
  }
  return parts.join(" and ") || "the supplied material";
}
